import { useState, useCallback } from 'react';
import usePlaces from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

import { Address } from '@electricjs/arc';

/**
 * Represents the result of an address validation operation.
 * Contains the formatted address and validated address components.
 */
export type AddressValidationResult = {
  /** The formatted address string from Google Places API */
  formattedAddress: string;
  /** The validated address components extracted from Google Places */
  addressComponents: Address;
  /** True when the validated address is identical to the original input */
  isExactMatch: boolean;
};

const normalizeField = (value: string | undefined | null): string =>
  (value || '').trim().toLowerCase();

export const addressesMatch = (a: Address, b: Address): boolean =>
  normalizeField(a.streetAddress1) === normalizeField(b.streetAddress1) &&
  normalizeField(a.streetAddress2) === normalizeField(b.streetAddress2) &&
  normalizeField(a.city) === normalizeField(b.city) &&
  normalizeField(a.state) === normalizeField(b.state) &&
  normalizeField(a.zip) === normalizeField(b.zip) &&
  normalizeField(a.country) === normalizeField(b.country);

export const consolidateAddress = (address: Address): string => {
  const parts = [
    address.streetAddress1,
    address.streetAddress2,
    address.city,
    address.state,
    address.zip,
    address.country,
  ].filter(Boolean);

  return parts.join(', ');
};

const extractAddressFromPlaceResult = (
  placeResult: google.maps.places.PlaceResult,
  originalAddress: Address
): Address => {
  const streetNumber = placeResult.address_components?.find(
    (item: google.maps.GeocoderAddressComponent) =>
      item.types.includes('street_number')
  )?.long_name;

  const streetName = placeResult.address_components?.find(
    (item: google.maps.GeocoderAddressComponent) => item.types.includes('route')
  )?.long_name;

  const city =
    placeResult.address_components?.find(
      (item: google.maps.GeocoderAddressComponent) =>
        item.types.includes('locality')
    )?.long_name ||
    placeResult.address_components?.find(
      (item: google.maps.GeocoderAddressComponent) =>
        item.types.includes('sublocality_level_1')
    )?.long_name ||
    placeResult.address_components?.find(
      (item: google.maps.GeocoderAddressComponent) =>
        item.types.includes('neighborhood')
    )?.long_name;

  const state = placeResult.address_components?.find(
    (item: google.maps.GeocoderAddressComponent) =>
      item.types.includes('administrative_area_level_1')
  )?.short_name;

  const countryData = placeResult.address_components?.find(
    (item: google.maps.GeocoderAddressComponent) =>
      item.types.includes('country')
  );

  const country = countryData?.short_name;

  const zip = placeResult.address_components?.find(
    (item: google.maps.GeocoderAddressComponent) =>
      item.types.includes('postal_code')
  )?.long_name;

  return {
    streetAddress1: streetName
      ? `${streetNumber || ''} ${streetName || ''}`.trim()
      : originalAddress.streetAddress1 || '',
    streetAddress2: originalAddress.streetAddress2 || '',
    city: city || originalAddress.city || '',
    state: state || originalAddress.state || '',
    country: country || originalAddress.country || '',
    zip: zip || originalAddress.zip || '',
  };
};

const getPlaceDetails = (
  placesService: google.maps.places.PlacesService | null,
  placeId: string,
  originalAddress: Address,
  addressString: string,
  onSuccess: (result: AddressValidationResult) => void,
  onError: (error: Error) => void
): void => {
  if (!placesService) {
    onError(new Error('Places service not available'));
    return;
  }

  placesService.getDetails(
    { placeId },
    (
      placeResult: google.maps.places.PlaceResult | null,
      status: google.maps.places.PlacesServiceStatus
    ) => {
      if (
        status === google.maps.places.PlacesServiceStatus.OK &&
        placeResult &&
        placeResult.address_components
      ) {
        const validatedAddress = extractAddressFromPlaceResult(
          placeResult,
          originalAddress
        );

        const validationResult: AddressValidationResult = {
          formattedAddress: placeResult.formatted_address || addressString,
          addressComponents: validatedAddress,
          isExactMatch: addressesMatch(originalAddress, validatedAddress),
        };

        onSuccess(validationResult);
      } else {
        onError(new Error(`PlacesServiceStatus: ${status}`));
      }
    }
  );
};

export const useAddressValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] =
    useState<AddressValidationResult | null>(null);

  /**
   * @NOTE: We use the direct Google Places services instead of the `usePlaces` hook's `getPlacePredictions`
   * because the hook has a race condition where `isPlacePredictionsLoading` becomes false before
   * predictions are available, causing UI to show empty results. The direct services provide
   * reliable callback-based results.
   */
  const { placesService, placesAutocompleteService } = usePlaces({}); // does not need the apiKey, because the library is being loaded from a script at "index.html"

  const validateAddress = useCallback(
    (
      address: Address,
      onSuccess: (result: AddressValidationResult | null) => void,
      onError: (error: Error) => void
    ): void => {
      // Allow validation even for incomplete addresses
      // The Google Places API will handle partial addresses and provide suggestions
      setIsValidating(true);
      setValidationResult(null);

      const addressString = consolidateAddress(address);

      // If the address string is empty or too short, show the modal with original address
      if (!addressString || addressString.trim().length < 3) {
        setIsValidating(false);
        setValidationResult(null);
        onSuccess(null);
        return;
      }

      placesAutocompleteService?.getPlacePredictions(
        { input: addressString },
        (predictions, status) => {
          if (status === 'OK' && predictions) {
            getPlaceDetails(
              placesService,
              predictions[0].place_id,
              address,
              addressString,
              (result: AddressValidationResult) => {
                setIsValidating(false);
                setValidationResult(result);
                onSuccess(result);
              },
              (error: Error) => {
                setIsValidating(false);
                setValidationResult(null);
                onError(error);
              }
            );
          } else {
            // Handle cases where no predictions were found (ZERO_RESULTS, NOT_FOUND, etc.)
            // This is not an error - it means the user should confirm their original address
            setIsValidating(false);
            setValidationResult(null);
            // Call onSuccess with null result to indicate no validation was possible
            onSuccess(null);
          }
        }
      );
    },
    [placesAutocompleteService, placesService]
  );

  return {
    /** Function to validate an address */
    validateAddress,
    /** Whether address validation is currently in progress */
    isValidating,
    /** The result of the most recent address validation */
    validationResult,
  };
};
